import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';

export interface CartItem {
  activityId: string;
  quantity: number;
  addedAt: number;
}

interface CartContextType {
  items: CartItem[];
  count: number;
  addItem: (activityId: string, quantity?: number) => void;
  removeItem: (activityId: string) => void;
  updateQuantity: (activityId: string, quantity: number) => void;
  clear: () => void;
  isInCart: (activityId: string) => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);
const STORAGE_KEY = 'palette_cart_v1';

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  // Sync across tabs
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try { setItems(JSON.parse(e.newValue)); } catch {}
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const addItem = useCallback((activityId: string, quantity = 1) => {
    setItems(prev => {
      const existing = prev.find(i => i.activityId === activityId);
      if (existing) {
        return prev.map(i => i.activityId === activityId ? { ...i, quantity: i.quantity + quantity } : i);
      }
      return [...prev, { activityId, quantity, addedAt: Date.now() }];
    });
  }, []);

  const removeItem = useCallback((activityId: string) => {
    setItems(prev => prev.filter(i => i.activityId !== activityId));
  }, []);

  const updateQuantity = useCallback((activityId: string, quantity: number) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(i => i.activityId !== activityId));
      return;
    }
    setItems(prev => prev.map(i => i.activityId === activityId ? { ...i, quantity } : i));
  }, []);

  const clear = useCallback(() => setItems([]), []);
  const isInCart = useCallback((id: string) => items.some(i => i.activityId === id), [items]);

  const count = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, count, addItem, removeItem, updateQuantity, clear, isInCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
